import { useMemo } from "react";
import { formatSecondsToDuration } from "../../../../../lib/utils/utility";

/**
 * Derives TopSites table rows from the per-site utilization data
 * already fetched by useFleetUtilization (no additional API calls).
 *
 * @param {Array} fleetData - Array of charger objects from ev_charger API
 * @param {object|undefined} perSite - Map of { [siteId]: utilizationResponse } from useFleetUtilization
 * @param {boolean} isLoading - Whether the parent utilization query is still loading
 * @returns {{ state: { isLoading, isError, data }, refresh: Function }}
 */
export default function useTopSitesReducer(fleetData, perSite, isLoading) {
  const rows = useMemo(() => {
    if (!fleetData?.length || !perSite) return [];

    // Group chargers by siteId and count per site
    const siteMap = {};
    fleetData.forEach(c => {
      const sid = c.siteId;
      if (!sid) return;
      if (!siteMap[sid]) {
        siteMap[sid] = { siteId: sid, chargerCount: 0, siteName: c.siteName || c.siteId || sid };
      }
      siteMap[sid].chargerCount += 1;
    });

    return Object.values(siteMap)
      .map(entry => {
        const u = perSite[entry.siteId];
        return {
          name: entry.siteName || String(entry.siteId),
          chargers: entry.chargerCount,
          sessions: u ? Number(u.totalSessions) || 0 : 0,
          energy: u ? Math.round((Number(u.totalEnergyConsumedKwh) || 0) * 10) / 10 : 0,
          utilizationTime: u ? formatSecondsToDuration(Number(u.totalUtilizationTimeSeconds) || 0) : "—",
          utilization: u ? Math.round(Number(u.utilizationPercent) || 0) : 0,
        };
      })
      .sort((a, b) => b.utilization - a.utilization);
  }, [fleetData, perSite]);

  return { state: { isLoading: !!isLoading, isError: false, data: rows }, refresh: () => {} };
}
